import React, { useState, useEffect } from 'react';
import { ArrowRight, GraduationCap, Compass, Quote, Star, Users, Globe2 } from 'lucide-react';

const HeroSection = () => {
  const quotes = [
    { text: "It always seems impossible until it's done.", author: "Nelson Mandela" },
    { text: "Education is the most powerful weapon which you can use to change the world.", author: "Nelson Mandela" },
    { text: "Pure mathematics is, in its way, the poetry of logical ideas.", author: "Albert Einstein" },
    { text: "Do not worry about your difficulties in Mathematics. I can assure you mine are still greater.", author: "Albert Einstein" },
    { text: "However difficult life may seem, there is always something you can do and succeed at.", author: "Stephen Hawking" },
    { text: "The only way to learn mathematics is to do mathematics.", author: "Paul Halmos" },
    { text: "Success is not final, failure is not fatal: it is the courage to continue that counts.", author: "Winston Churchill" },
    { text: "Education is not the filling of a pail, but the lighting of a fire.", author: "W.B. Yeats" }
  ];

  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsVisible(false);
      setTimeout(() => {
        setCurrentQuoteIndex((prev) => (prev + 1) % quotes.length);
        setIsVisible(true);
      }, 500);
    }, 6000);
    return () => clearInterval(interval);
  }, [quotes.length]);

  const currentQuote = quotes[currentQuoteIndex];

  return (
    <section className="hero-section" data-testid="hero-section" style={{
      background: '#FFFCF5',
      minHeight: '100vh',
      paddingTop: '100px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Subtle noise texture */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        opacity: 0.025, pointerEvents: 'none'
      }} />

      {/* Rotating Quote Banner - Navy */}
      <div style={{
        background: '#0F172A',
        padding: '1rem 2rem',
        textAlign: 'center',
        borderBottom: '2px solid #C5A059',
        marginBottom: '3rem'
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: '1rem', flexWrap: 'wrap',
          opacity: isVisible ? 1 : 0, transition: 'opacity 0.5s ease-in-out',
          maxWidth: '900px', margin: '0 auto'
        }}>
          <Quote size={18} color="#C5A059" style={{ transform: 'rotate(180deg)', flexShrink: 0 }} />
          <span style={{
            color: '#FFFCF5', fontSize: 'clamp(0.85rem, 2vw, 1rem)',
            fontFamily: "'Playfair Display', Georgia, serif",
            fontStyle: 'italic', letterSpacing: '0.3px', lineHeight: 1.5
          }}>
            "{currentQuote.text}"
          </span>
          <Quote size={18} color="#C5A059" style={{ flexShrink: 0 }} />
        </div>
        <span style={{
          color: '#C5A059', fontSize: 'clamp(0.75rem, 1.5vw, 0.85rem)',
          fontWeight: 500, marginTop: '0.5rem', display: 'block',
          opacity: isVisible ? 1 : 0, transition: 'opacity 0.5s ease-in-out'
        }}>
          — {currentQuote.author}
        </span>
      </div>

      <div style={{
        maxWidth: '1280px',
        margin: '0 auto',
        padding: '2rem 2rem 5rem',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
        gap: '4rem',
        alignItems: 'center'
      }}>
        {/* LEFT: Editorial Dual-Headline */}
        <div style={{ textAlign: 'left' }}>
          {/* Elite Badge */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
            background: 'transparent',
            color: '#0F172A',
            padding: '0.5rem 0',
            marginBottom: '1.5rem',
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            fontFamily: "'Inter', sans-serif",
            borderBottom: '1px solid #C5A059',
            paddingBottom: '0.75rem'
          }}>
            <Compass size={14} color="#C5A059" />
            <span>Oxford PGCE · Team GB · Cambridge Examiner</span>
          </div>

          {/* PRIMARY: Travel Tutor Headline */}
          <h1 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(2.4rem, 5vw, 4rem)',
            fontWeight: 700,
            color: '#0F172A',
            lineHeight: 1.05,
            marginBottom: '1rem',
            letterSpacing: '-0.03em'
          }}>
            Elite In-Residence
            <br />
            <span style={{ color: '#C5A059', fontStyle: 'italic' }}>Travel Tutor</span>
            <br />
            <span style={{ fontSize: 'clamp(1.4rem, 3vw, 2rem)', fontWeight: 500, color: '#475569' }}>
              for Discerning International Families
            </span>
          </h1>

          <p style={{
            color: '#475569', fontSize: '1.1rem', lineHeight: 1.7,
            marginBottom: '2rem', maxWidth: '540px',
            fontFamily: "'Inter', sans-serif"
          }}>
            Bespoke academic mentorship and pastoral guidance — worldwide.
            <strong style={{ color: '#0F172A' }}> Dr. Michael Omakobia</strong> partners with high-net-worth families
            requiring an outstanding in-house educator across residences, tournaments, and international relocations.
          </p>

          {/* SECONDARY: Local GCSE strapline */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.75rem',
            padding: '0.75rem 1rem', marginBottom: '2rem',
            borderLeft: '3px solid #C5A059',
            background: 'rgba(197, 160, 89, 0.06)'
          }}>
            <GraduationCap size={20} color="#C5A059" />
            <span style={{ color: '#0F172A', fontSize: '0.95rem', fontFamily: "'Inter', sans-serif" }}>
              <strong>Also offering</strong> world-class GCSE & IGCSE Mathematics tuition — online and in London
            </span>
          </div>

          {/* CTAs */}
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
            <a
              href="#travel-tutor"
              data-testid="hero-enquire-btn"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                background: '#0F172A', color: '#FFFCF5',
                padding: '1rem 2rem', borderRadius: '4px',
                textDecoration: 'none', fontWeight: 600,
                fontFamily: "'Inter', sans-serif",
                border: '1px solid #0F172A',
                transition: 'all 0.3s ease',
                letterSpacing: '0.02em'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#C5A059'; e.currentTarget.style.color = '#0F172A'; e.currentTarget.style.borderColor = '#C5A059'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#0F172A'; e.currentTarget.style.color = '#FFFCF5'; e.currentTarget.style.borderColor = '#0F172A'; }}
            >
              Enquire Confidentially
              <ArrowRight size={18} />
            </a>
            <a
              href="#pricing"
              data-testid="hero-view-pricing"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                background: 'transparent', color: '#0F172A',
                padding: '1rem 2rem', borderRadius: '4px',
                textDecoration: 'none', fontWeight: 600,
                fontFamily: "'Inter', sans-serif",
                border: '1px solid #0F172A',
                transition: 'all 0.3s ease',
                letterSpacing: '0.02em'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#0F172A'; e.currentTarget.style.color = '#FFFCF5'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#0F172A'; }}
            >
              View Local Tuition Pricing
            </a>
          </div>

          {/* Trust Indicators */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '2rem',
            flexWrap: 'wrap', paddingTop: '1.25rem',
            borderTop: '1px solid rgba(15, 23, 42, 0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Globe2 size={18} color="#C5A059" />
              <span style={{ color: '#475569', fontSize: '0.9rem' }}>
                <strong style={{ color: '#0F172A' }}>15+</strong> countries visited
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Star size={18} color="#C5A059" fill="#C5A059" />
              <span style={{ color: '#475569', fontSize: '0.9rem' }}>
                <strong style={{ color: '#0F172A' }}>90%</strong> achieve Grade 8-9
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Users size={18} color="#C5A059" />
              <span style={{ color: '#475569', fontSize: '0.9rem' }}>
                <strong style={{ color: '#0F172A' }}>100+</strong> students mentored
              </span>
            </div>
          </div>
        </div>

        {/* RIGHT: Portrait + Success Badge (navy/gold only) */}
        <div style={{ position: 'relative', textAlign: 'center' }}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <img
              src="https://customer-assets.emergentagent.com/job_tutor-abba/artifacts/1zxnvv6b_Screenshot_20260114_125045_Chrome.jpg"
              alt="Dr. Michael Omakobia"
              style={{
                width: '320px', height: '380px',
                borderRadius: '4px',
                objectFit: 'cover', objectPosition: 'center top',
                boxShadow: '0 20px 60px rgba(15, 23, 42, 0.18)',
                border: '1px solid rgba(15, 23, 42, 0.08)'
              }}
            />
            <div style={{
              position: 'absolute', top: '-14px', right: '-14px',
              background: '#C5A059', color: '#0F172A',
              padding: '0.6rem 1.1rem', borderRadius: '4px',
              fontWeight: 700, fontSize: '0.75rem',
              letterSpacing: '0.1em', textTransform: 'uppercase',
              boxShadow: '0 8px 24px rgba(197, 160, 89, 0.4)',
              fontFamily: "'Inter', sans-serif"
            }}>
              IGCSE Examiner
            </div>

            {/* Latest Success — gold/cream (no green) */}
            <div style={{
              position: 'absolute', bottom: '-24px', left: '50%',
              transform: 'translateX(-50%)',
              background: '#FFFCF5',
              padding: '0.9rem 1.5rem',
              borderRadius: '4px',
              boxShadow: '0 12px 32px rgba(15, 23, 42, 0.12)',
              border: '2px solid #C5A059',
              minWidth: '280px'
            }}>
              <div style={{ marginBottom: '0.4rem' }}>
                <span style={{
                  background: '#0F172A', color: '#C5A059',
                  padding: '3px 10px', borderRadius: '2px',
                  fontSize: '0.65rem', fontWeight: 700,
                  letterSpacing: '0.12em', textTransform: 'uppercase',
                  fontFamily: "'Inter', sans-serif"
                }}>
                  Latest Success
                </span>
              </div>
              <p style={{
                color: '#0F172A', fontWeight: 600, margin: 0,
                fontSize: '0.95rem', fontFamily: "'Inter', sans-serif"
              }}>
                Anja: Failing → Grade 5 in <span style={{ color: '#C5A059' }}>3.5 months</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
